import { Select } from "antd";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../root-store-context";
import useBreakpoint from "use-breakpoint";
import {
  RouteElement,
  RouteGroupElement,
} from "../../../stores/appStores/ApplicationStore";
import { useLocation, useNavigate } from "react-router-dom";
import { CommunityProxy } from "../../../api/Common/CommonTypes";

const NavCommunity = observer(() => {
  const { applicationStore } = useStore();
  const rootStore = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  const BREAKPOINTS = { mobile: 0, desktop: 700 };
  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");

  const onChangeCommunity = async (id: string) => {
    const newCommunity = applicationStore.CommunityList.find(
      (el) => el.id.toString() === id
    );
    if (!newCommunity) return;

    // Refresh the IdP cookie for the new community so downstream SSO hand-offs
    // (eTrak, ABDI) pick up the new resident context. Persist addressId so the
    // post-redirect bootstrap restores the new community.
    localStorage.setItem("addressId", newCommunity.id.toString());

    // Go to the IdP FIRST, then let the post-redirect reload blink the screen
    // to refresh on-screen data — not the other way round. We mint the URL by
    // passing the new community straight to the API call instead of mutating
    // the observable store: touching configApi here would re-render the SPA
    // ("blink") before we ever leave for the IdP. The store stays untouched on
    // this happy path; the reload after the IdP bounces back rebuilds it once.
    //
    // Always return to the home page after the refresh — returning to the exact
    // page proved unreliable, and "/" is a clean app route with no risk of
    // echoing back a spent OAuth `state` from the login callback.
    const refreshUrl = await rootStore.apiStore.AssociationApiClient
      .getTokenRefreshUrl("/", newCommunity)
      .catch(() => null);
    if (refreshUrl) {
      window.location.href = refreshUrl;
      return;
    }

    // Fallback: URL couldn't be minted (back-end unreachable, feature off).
    // Update in place so the UX still recovers without navigating.
    // setSelectedCommunityById -> setSelectedCommunity already points configApi
    // at the new community and rebuilds the dependent stores, so we don't touch
    // configApi here — doing so would add a redundant intermediate re-render.
    rootStore.clearLocalStorages();
    await applicationStore.setSelectedCommunityById(newCommunity.id.toString());
    await rootStore.refreshStores();

    const isExistRoute: boolean[] = [];
    applicationStore.routingList.forEach((item) =>
      isExistRoute.push(isPathExists(item, location.pathname))
    );
    if (isExistRoute.includes(true)) {
      return;
    } else {
      let isBoolean = false;
      applicationStore.routingList.forEach((item) => {
        if (!isBoolean)
          if (item?.children) {
            item?.children.forEach((i: RouteElement) => {
              if (!isBoolean && applicationStore.isFeatures(i.featureID)) {
                isBoolean = true;
                navigate(`${item.path}${i.path}`);
                return;
              }
            });
          }
      });
    }
  };

  const isPathExists = (
    routeGroup: RouteGroupElement,
    path: string
  ): boolean => {
    const pathSplit = path.split("/");
    const tPath = "/" + pathSplit[pathSplit.length - 1];
    return !!routeGroup.children?.some(
      (child: RouteElement) =>
        child.path === tPath && applicationStore.isFeatures(child.featureID)
    );
  };

  return (
    <Select
      value={applicationStore.SelectedAddress.id.toString()}
      style={breakpoint === "mobile" ? { width: "70vw", maxWidth: 500 } : { maxWidth: 500, minWidth: 210 }}
      showSearch
      filterOption={(
        input: string,
        option?: { label: string; value: number }
      ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
      options={applicationStore.CommunityList?.map(
        (i: CommunityProxy, index) =>
        ({
          value: String(i.id),
          label: `${i.address} (${i.name})`,
          key: index,
        } as any)
      )}
      onChange={onChangeCommunity}
    />
  );
});

export default NavCommunity;
